import { useState } from 'react'
import { generateUUID } from '#app/utils/chat-query.js'

/**
 * Per-tab chat session id, used as the join key for Snabbsök implicit-feedback
 * logging (impressions + clicks). Lives in `sessionStorage` so it survives
 * Snabbsök queries within one tab and disappears when the tab closes — there
 * is NO cookie, NO localStorage, NO link to any account, NO cross-session
 * linkage. The design (search_feedback_recommendation.md §3) deliberately
 * picked the smallest-surface anchor that still makes per-query click
 * sequences attributable.
 *
 * SSR safety: `sessionStorage` is undefined during the Remix server render;
 * we return an empty string in that pass so the loader doesn't fire the
 * impression INSERT against a bogus session id (the loader's
 * `if (sessionId && impressionId && …)` guard turns this into a no-op).
 * Once the client mounts, the real id is minted and replaces the empty value
 * on the next state read — the existing fetch effect re-runs on `query` so
 * the first real fetch always carries the real id.
 *
 * The id is minted exactly ONCE per tab: `useState(() => …)` runs the
 * initializer only on the first render; subsequent reads return the same
 * value. `generateUUID()` reuses the project's existing v4 helper
 * (`crypto.randomUUID()` with a non-secure-context fallback for HTTP dev).
 */
const STORAGE_KEY = 'snabbsok-session-id'

export function useChatSessionId(): string {
	const [id] = useState(() => {
		if (typeof window === 'undefined') return ''
		try {
			let s = window.sessionStorage.getItem(STORAGE_KEY)
			if (!s) {
				s = generateUUID()
				window.sessionStorage.setItem(STORAGE_KEY, s)
			}
			return s
		} catch {
			// sessionStorage unavailable (private mode in some browsers, or
			// strict storage settings): mint a process-local id so the rest of
			// the wiring still has a stable string within this render tree.
			// The id won't persist across reloads — acceptable degraded mode.
			return generateUUID()
		}
	})
	return id
}
